/**
 * Vision 2040 Translations Module (Portuguese)
 * @module vision2040
 * @language pt
 */

export const vision2040 = {
  vision2040Hero: {
    membersTarget: "Meta de Membros",
    countryTarget: "Meta de Países",
    annualBeneficiaries: "Beneficiários Anuais"
  },
  vision2040TechTargets: {
    aiUsers: "400K+ usuários",
    vrTrainers: "10K+ treinadores",
    appDownloads: "300K+ downloads",
    sustainabilityCountries: "80 países"
  },
  // Root level strategicPillarsData - components use t('strategicPillarsData.xxx')
  strategicPillarsData: {
    expandTraining: "Expandir Programas de Treinamento de Liderança",
    certifiedTrainers: "10.000 Treinadores Certificados",
    goldStandard: "Currículo Padrão Ouro",
    trainingTarget: "100K treinados anualmente até 2040",
    expandDetail1: "Escalar JCI Academy para 130+ países",
    expandDetail2: "Desenvolver 10K treinadores certificados globalmente",
    expandDetail3: "Lançar plataforma de treinamento multilíngue",
    expandDetail4: "Criar trilhas de liderança específicas por indústria",
    expandDetail5: "Estabelecer parcerias com universidades",
    trainedYear: "Treinados/Ano",
    aiDashboard: "Painel com IA",
    offlineApp: "App Móvel Offline-First",
    impactTracking: "Rastreamento de Impacto em Tempo Real",
    techTarget: "80% de adoção digital até 2040",
    techDetail1: "Implementar plataforma de experiência do membro com IA",
    techDetail2: "Lançar programa de simulação de liderança VR",
    techDetail3: "Criar painel de dados global unificado",
    techDetail4: "Implementar verificação de impacto blockchain",
    techDetail5: "Construir plataforma mobile-first acessível",
    users: "Usuários",
    climateAction: "Integração de Ação Climática",
    carbonNeutral: "Operações Carbon Neutral",
    renewableEnergy: "100% Energia Renovável",
    sustainabilityTarget: "Net-zero até 2030",
    sustainDetail1: "Alcançar operações carbon neutral até 2030",
    sustainDetail2: "Integrar mapeamento ODS para todos os projetos",
    sustainDetail3: "Criar programa de certificação verde",
    sustainDetail4: "Parceria com organizações ambientais",
    sustainDetail5: "Desenvolver currículo de alfabetização climática",
    riseProjects: "Projetos RISE",
    genderParity: "Alcance da Paridade de Gênero",
    expandUnderrepresented: "Expandir para Regiões Sub-representadas",
    financialAccess: "Programas de Acesso Financeiro",
    equityTarget: "50% paridade de gênero até 2035",
    equityDetail1: "Alcançar 50% paridade de gênero na liderança",
    equityDetail2: "Expandir presença em regiões carentes",
    equityDetail3: "Criar programas de bolsas para acesso",
    equityDetail4: "Desenvolver frameworks de liderança inclusivos",
    equityDetail5: "Construir infraestrutura de acessibilidade"
  },
  simulator: {
    factors: {
      conservativeGrowth: "Crescimento anual de 2-3% de membros",
      limitedDigital: "Adoção limitada de plataforma digital",
      minimalPartnerships: "Mínimas novas parcerias corporativas",
      regionalChallenges: "Desafios de expansão regional",
      moderateGrowth: "Crescimento anual de 5-7% de membros",
      fullDigital: "Implementação completa de plataforma digital",
      strategicPartnerships: "Parcerias corporativas estratégicas",
      regionalExpansion: "Expansão regional coordenada",
      aggressiveGrowth: "Crescimento anual de 10-15% de membros",
      aiEngagement: "Engajamento de membros com IA",
      youthMovement: "Expansão viral do movimento juvenil",
      corporateSponsors: "Grandes patrocinadores corporativos"
    },
    risks: {
      targetsMissed: "Metas da Visão 2040 podem não ser alcançadas",
      reducedInfluence: "Influência e alcance global reduzidos",
      talentGaps: "Lacunas de talentos em regiões carentes",
      sustainedInvestment: "Investimento sustentado necessário",
      techInfrastructure: "Infraestrutura tecnológica requerida",
      regionalLeadership: "Liderança regional forte necessária",
      strainResources: "Recursos podem ficar sobrecarregados",
      qualityQuantity: "Equilíbrio qualidade vs quantidade",
      governanceChallenges: "Desafios de governança"
    }
  },
  vision2040: {
    title: "JCI Visão 2040",
    pageSubtitle: "Roteiro Estratégico para 500.000 Jovens Líderes",
    transformingFuture: "Transformando o Futuro da Liderança Jovem",
    followProgress: "Acompanhe nosso progresso em direção a uma rede global de 500.000 jovens líderes criando impacto positivo sustentável até 2040.",
    exploreVision: "Explorar Visão 2040",
    
    // Tab Labels
    fiiImpact: "Impacto FII",
    timeline: "Linha do Tempo",
    simulator: "Simulador",
    globalView: "Visão Global",
    
    // FII Impact Section
    fiiImpactTitle: "Impacto FII até 2040",
    fiiImpactDesc: "Melhorias projetadas do Índice de Investimento Futuro se a JCI executar a estratégia Visão 2040",
    globalFIIPoints: "Pontos FII Globais",
    africaMeFII: "Pontos FII África/OM",
    inequalityReduction: "Redução de Desigualdade",
    regionalGapClosure: "Fechamento de lacuna regional",
    highest: "mais alto",
    mentalHealthPillar: "Saúde Mental",
    workforceDevelopment: "Desenvolvimento da Força de Trabalho",
    entrepreneurshipPillar: "Empreendedorismo",
    projectedReach: "Alcance Projetado",
    fiiImpactLabel: "Impacto FII",
    primaryFocus: "Foco primário",
    regionalFIIProjectionsTitle: "Projeções FII Regionais: 2025 vs 2040",
    current2025: "Atual 2025",
    projected2040: "Projetado 2040",
    maintainLead: "Manter liderança",
    strongGrowth: "Forte crescimento",
    accelerating: "Acelerando",
    highestImprovement: "Maior melhoria",
    sourceFII: "Fonte: Plano Estratégico JCI Visão 2040 | Projeções do Índice de Investimento Futuro 2024",
    
    // Simulator Section
    simulatorTitle: "Simulador de Cenários Visão 2040",
    simulatorSubtitle: "Explore três futuros possíveis para a JCI. O que acontece se excedermos as expectativas—ou ficarmos aquém?",
    baseline2025: "Baseline 2025",
    conservative: "Conservador",
    conservativeDesc: "E se o crescimento estagnar? Adoção limitada de tecnologia, parcerias mínimas, ventos contrários econômicos.",
    moderate: "Moderado (Meta)",
    moderateDesc: "Trajetória de crescimento planejada com adoção digital esperada e parcerias estratégicas.",
    aggressive: "Agressivo",
    aggressiveDesc: "Crescimento máximo com adoção completa de tecnologia, parcerias extensas e expansão viral.",
    probability: "Probabilidade",
    scenarioOutcomes: "Resultados do Cenário",
    vsTarget: "vs meta",
    onTarget: "No Alvo",
    keySuccessFactors: "Fatores-Chave de Sucesso",
    risksAndChallenges: "Riscos e Desafios",
    growthTrajectory: "Comparação de Trajetória de Crescimento",
    scenario: "Cenário",
    targetModerate: "Meta (Moderado)",
    comparisonSummary: "Resumo de Comparação de Cenários",
    metric: "Métrica",
    baseline: "Baseline",
    target2040: "Meta 2040",
    sourceSimulator: "Fonte: Plano Estratégico JCI Visão 2040 | Cenários são projeções ilustrativas",
    
    // Growth Visualization Section
    growthVisualization: "Visualização de Crescimento",
    growthDesc: "Trajetória de crescimento estratégico do baseline 2025 até as metas 2040",
    growthVisualizationDesc: "Trajetória de crescimento estratégico do baseline 2025 até as metas 2040",
    progress: "Progresso",
    members: "Membros",
    countries: "Países",
    localOrganizations: "Organizações Locais",
    annualProjects: "Projetos Anuais",
    annualBeneficiaries: "Beneficiários Anuais",
    current: "Atual",
    target: "Meta",
    growth: "Crescimento",
    cagr: "CAGR",
    cagrMembers: "~8-10% TCAC",
    nationsGrowth: "+86 nações",
    expansionRegions: "África Subsaariana, Oriente Médio, Sudeste Asiático",
    moreGrassrootsHubs: "Mais centros de base",
    projectOrgAverage: "~1 projeto/org em média",
    youthPopulation: "~1,2% da população jovem global",
    
    // Priority levels
    priority: {
      critical: "Crítico",
      high: "Alto",
      medium: "Médio",
      low: "Baixo"
    },
    
    // Global Presence Section
    globalPresence: "Presença Global JCI",
    globalMembers: "Membros Globais",
    activeCountries: "Países Ativos",
    averageGrowth: "Crescimento Médio",
    regionalDistribution: "Distribuição Regional",
    globalPerformance: "Desempenho Global",
    performanceComparison: "Comparação entre desempenho atual vs metas da Visão 2040",
    detailedAnalysis: "Análise Regional Detalhada",
    featuredCountries: "Países em Destaque",
    achievements: "Conquistas",
    challenges: "Desafios",
    
    // Main Initiatives Section
    mainInitiatives: "Principais Iniciativas",
    leadershipDevelopment: "Excelência em Desenvolvimento de Liderança",
    technologyInnovation: "Tecnologia e Inovação Digital",
    sustainabilityFocus: "Foco em Sustentabilidade e Impacto",
    globalNetworkGrowth: "Expansão da Rede Global",
    
    // Strategic Pillars Section
    strategicPillars: "Pilares Estratégicos",
    strategicPillarsTitle: "4 Pilares Estratégicos para 2040",
    strategicPillarsSubtitle: "Abordagem integrada para transformar a oportunidade dos jovens globalmente",
    strategicPillarsDesc: "Nosso roteiro é construído em quatro pilares interconectados que juntos transformarão a oportunidade dos jovens mundialmente",
    viewAllInitiatives: "Ver Todas as Iniciativas",
    allInitiatives: "Todas as Iniciativas",
    
    pillar1Title: "Saúde Mental e Bem-estar",
    pillar2Title: "Desenvolvimento da Força de Trabalho e Habilidades",
    pillar3Title: "Empreendedorismo e Inovação",
    pillar4Title: "Parceria Global e Escalabilidade",
    
    sustainability: "Sustentabilidade",
    equityInclusion: "Equidade e Inclusão",
    current2040Target: "Atual → Meta 2040",
    
    // Milestone Roadmap Section
    milestoneRoadmap: "Roteiro de Marcos",
    milestoneDesc: "Acompanhe nossa jornada do estado atual até a conquista da Visão 2040",
    currentState: "Estado Atual",
    currentStateDesc: "Nosso ponto de partida - 148.000 membros em 114 países",
    midpointTarget: "Meta Intermediária",
    midpointTargetDesc: "Marco do meio - 300.000 membros em 150 países",
    accelerationPhase: "Fase de Aceleração",
    accelerationPhaseDesc: "Escalada rápida - 400.000 membros em 175 países",
    visionAchieved: "Visão Alcançada",
    visionAchievedDesc: "Meta completa - 500.000 membros em 200 países",
    inProgress: "Em Progresso",
    upcoming: "Próximo",
    completed: "Concluído",
    planned: "Planejado",
    orgs: "Orgs",
    projects: "Projetos",
    beneficiaries: "Beneficiários",
    keyInitiatives: "Iniciativas-Chave",
    needed: "Necessário",
    growthRequired: "crescimento necessário",
    sourceStrategicPlan: "Fonte: Plano Estratégico JCI Visão 2040",
    
    // Timeline Section
    timelineTitle: "Linha do Tempo Visão 2040",
    yearsRemaining: "Anos Restantes",
    historicalProgress: "Progresso Histórico",
    jciFounded: "JCI Fundada",
    foundedDesc: "Junior Chamber International estabelecida em St. Louis, Missouri",
    globalExpansion: "Expansão Global",
    globalExpansionDesc: "Expansão para 100+ países mundialmente",
    foundationYear: "Ano de Fundação",
    foundationYearDesc: "Transformação digital e escalada do programa RISE",
    programs: "Programas",
    implementationProgress: "Progresso de Implementação",
    
    // Digital Platform Section
    digitalPlatform: "Plataforma Digital",
    techTarget: "Meta: 80%+ dos membros usando plataforma digital até 2040",
    aiIntegration: "Integração de IA",
    aiDesc: "Experiência de membro personalizada e recomendações de programa",
    vrTraining: "Treinamento de Liderança VR",
    vrTrainingDesc: "Simulações imersivas para desenvolvimento de liderança",
    aiDashboard: "Painel com IA",
    aiDashboardDesc: "Experiência personalizada do membro com recomendações de IA",
    
    // Sustainability Goals Section
    sustainabilityGoals: "Objetivos de Sustentabilidade",
    carbonNeutral: "Operações Carbon Neutral",
    carbonNeutralDesc: "100% compensação de carbono até 2030",
    sdgAlignment: "Alinhamento ODS",
    sdgDesc: "Todos os programas mapeados para Objetivos de Desenvolvimento Sustentável da ONU",
    
    // Tech Roadmap Section
    techRoadmap: "Roteiro Tecnológico",
    techRoadmapDesc: "Marcos de transformação digital no caminho para 2040",
    mobileApp: "Aplicativo Móvel",
    mobileAppDesc: "Experiência móvel offline-first para todos os membros",
    sustainabilityTracking: "Rastreamento de Sustentabilidade",
    sustainabilityTrackingDesc: "Monitoramento de impacto ambiental em tempo real",
    complete: "Completo"
  },
  vision2040Extended: {
    fiiImpactTab: "Impacto FII",
    timeline: "Linha do Tempo",
    simulator: "Simulador",
    globalView: "Visão Global",
    milestoneTitle: "Roteiro de Marcos para 2040",
    milestoneSubtitle: "Metas-chave e indicadores de progresso em quatro fases estratégicas",
    milestoneRoadmap: "Roteiro de Marcos",
    milestoneRoadmapDesc: "Acompanhe nossa jornada do estado atual até a conquista da Visão 2040",
    currentState: "Estado Atual",
    currentStateDesc: "Nosso ponto de partida - 175.000 membros em 115 países",
    midpointTarget: "Meta Intermediária",
    midpointTargetDesc: "Marco do meio - 300.000 membros em 130 países",
    accelerationPhase: "Fase de Aceleração",
    accelerationPhaseDesc: "Escalada rápida - 400.000 membros em 140 países",
    visionAchieved: "Visão Alcançada",
    visionAchievedDesc: "Meta completa - 500.000 membros em 150 países",
    inProgress: "Em Progresso",
    upcoming: "Próximo",
    completed: "Concluído",
    planned: "Planejado",
    target: "Meta",
    members: "Membros",
    countries: "Países",
    localOrganizations: "Organizações Locais",
    orgs: "Orgs",
    projects: "Projetos",
    beneficiaries: "Beneficiários",
    keyInitiatives: "Iniciativas-Chave",
    current: "Atual",
    needed: "Necessário",
    growthRequired: "crescimento necessário",
    sourceStrategicPlan: "Fonte: Plano Estratégico JCI Visão 2040",
    fiiImpactTitle: "Impacto FII até 2040",
    fiiImpactDesc: "Melhorias projetadas do Índice de Investimento Futuro se a JCI executar a estratégia Visão 2040",
    globalFIIPoints: "Pontos FII Globais",
    africaMeFII: "Pontos FII África/OM",
    inequalityReduction: "Redução de Desigualdade",
    regionalGapClosure: "Fechamento de lacuna regional",
    highest: "mais alto",
    mentalHealthPillar: "Saúde Mental",
    workforceDevelopment: "Desenvolvimento da Força de Trabalho",
    entrepreneurshipPillar: "Empreendedorismo",
    projectedReach: "Alcance Projetado",
    fiiImpactLabel: "Impacto FII",
    primaryFocus: "Foco primário",
    regionalFIIProjectionsTitle: "Projeções FII Regionais: 2025 vs 2040",
    current2025: "Atual 2025",
    projected2040: "Projetado 2040",
    maintainLead: "Manter liderança",
    strongGrowth: "Forte crescimento",
    accelerating: "Acelerando",
    highestImprovement: "Maior melhoria",
    sourceFII: "Fonte: Plano Estratégico JCI Visão 2040 | Projeções do Índice de Investimento Futuro 2024",
    timelineTitle: "Linha do Tempo Visão 2040",
    timelineDesc: "Contexto histórico e marcos futuros em nossa jornada até 2040",
    yearsRemaining: "Anos Restantes",
    historicalProgress: "Progresso Histórico",
    jciFounded: "JCI Fundada",
    foundedDesc: "Junior Chamber International estabelecida",
    globalExpansion: "Expansão Global",
    globalExpansionDesc: "Expansão para 100+ países mundialmente",
    foundationYearDesc: "Transformação digital e escalada do programa RISE",
    programs: "Programas",
    implementationProgress: "Progresso de Implementação",
    simulatorTitle: "Simulador de Cenários Visão 2040",
    simulatorSubtitle: "Explore três futuros possíveis para a JCI. O que acontece se excedermos as expectativas—ou ficarmos aquém?",
    baseline2025: "Baseline 2025",
    conservative: "Conservador",
    conservativeDesc: "E se o crescimento estagnar? Adoção limitada de tecnologia, parcerias mínimas, ventos contrários econômicos.",
    moderate: "Moderado (Meta)",
    moderateDesc: "Trajetória de crescimento planejada com adoção digital esperada e parcerias estratégicas.",
    aggressive: "Agressivo",
    aggressiveDesc: "Crescimento máximo com adoção completa de tecnologia, parcerias extensas e expansão viral.",
    probability: "Probabilidade",
    scenarioOutcomes: "Resultados do Cenário",
    annualProjects: "Projetos Anuais",
    vsTarget: "vs meta",
    onTarget: "No Alvo",
    keySuccessFactors: "Fatores-Chave de Sucesso",
    risksAndChallenges: "Riscos e Desafios",
    growthTrajectory: "Comparação de Trajetória de Crescimento",
    scenario: "Cenário",
    targetModerate: "Meta (Moderado)",
    comparisonSummary: "Resumo de Comparação de Cenários",
    metric: "Métrica",
    baseline: "Baseline",
    target2040: "Meta 2040",
    sourceSimulator: "Fonte: Plano Estratégico JCI Visão 2040 | Cenários são projeções ilustrativas para fins de planejamento estratégico",
    simulatorData: {
      factors: {
        conservativeGrowth: "Crescimento anual de 2-3% de membros",
        limitedDigital: "Adoção limitada de plataforma digital",
        minimalPartnerships: "Mínimas novas parcerias corporativas",
        moderateGrowth: "Crescimento anual de 5-7% de membros",
        fullDigital: "Implementação completa de plataforma digital",
        strategicPartnerships: "Parcerias corporativas estratégicas",
        aggressiveGrowth: "Crescimento anual de 10-15% de membros",
        aiEngagement: "Engajamento de membros com IA",
        youthMovement: "Expansão viral do movimento juvenil"
      },
      risks: {
        targetsMissed: "Metas da Visão 2040 podem não ser alcançadas",
        reducedInfluence: "Influência e alcance global reduzidos",
        talentGaps: "Lacunas de talentos em regiões carentes",
        moderateRisk: "Risco moderado de execução permanece",
        resourceConstraints: "Restrições de recursos possíveis",
        competitionRisk: "Concorrência de outras organizações",
        executionChallenges: "Desafios significativos de execução",
        fundingRequirements: "Altos requisitos de financiamento",
        marketChanges: "Condições de mercado podem mudar"
      }
    },
    globalPresenceTitle: "Presença Global JCI",
    globalMembers: "Membros Globais",
    activeCountries: "Países Ativos",
    averageGrowth: "Crescimento Médio",
    regionalDistribution: "Distribuição Regional",
    globalPerformance: "Desempenho Global",
    performanceComparison: "Comparação entre desempenho atual vs metas da Visão 2040",
    detailedAnalysis: "Análise Regional Detalhada",
    achievements: "Conquistas",
    challenges: "Desafios",
    growth: "Crescimento",
    priority: { critical: "Crítico", high: "Alto", medium: "Médio", low: "Baixo" },
    milestoneInitiatives: {
      year2025: { initiative1: "Expansão da Plataforma Digital", initiative2: "Continuação do programa RISE", initiative3: "Baseline FII estabelecido", initiative4: "Estratégias de crescimento regional" },
      year2030: { initiative1: "Operações carbon-neutral", initiative2: "Painel de membros com IA", initiative3: "50% paridade de gênero alcançada", initiative4: "LMS digital totalmente implantado" },
      year2035: { initiative1: "10.000+ treinadores certificados", initiative2: "Treinamento de liderança VR implantado", initiative3: "Lacuna FII África/OM 50% fechada", initiative4: "Aplicativo móvel 300K+ downloads" },
      year2040: { initiative1: "Rede de 500K jovens líderes", initiative2: "FII global melhorou 4-7 pontos", initiative3: "80%+ adoção de plataforma digital", initiative4: "Oportunidade dos jovens transformada" }
    },
    pillarData: {
      mentalHealth: { current: "145 projetos RISE", projection: "5.000+ projetos RISE", reach: "50M+ alcançados", fiiImpact: "+2-3 pontos", focus: "Lacuna de saúde mental África/OM" },
      workforce: { current: "~50K treinados", projection: "200K+ jovens treinados", reach: "80% taxa de emprego", fiiImpact: "+1-2 pontos", focus: "Caminhos STEM/digitais" },
      entrepreneurship: { current: "1K empreendedores", projection: "50K+ apoiados", reach: "$500M+ capital mobilizado", fiiImpact: "+1-2 pontos", focus: "100K+ treinados" }
    },
    regionNames: { europe: "Europa", asiaPacific: "Ásia e o Pacífico", americas: "América", africaME: "África e Oriente Médio" },
    radarSubjects: { leadership: "Liderança", technology: "Tecnologia", sustainability: "Sustentabilidade", partnerships: "Parcerias", innovation: "Inovação", impact: "Impacto" },
    globalRegions: {
      americas: { name: "América", achievements: ["8,0% da membresia global", "Forte rede LO"], challenges: ["Retenção de membros", "Diversidade regional"] },
      europe: { name: "Europa", achievements: ["11,1% da membresia global", "Liderança em sustentabilidade"], challenges: ["Demografia envelhecendo", "Estagnação de crescimento"] },
      asiaPacific: { name: "Ásia e o Pacífico", achievements: ["70,7% da membresia global", "Maior potencial de crescimento"], challenges: ["Barreiras culturais", "Infraestrutura digital"] },
      africaME: { name: "África e Oriente Médio", achievements: ["10,3% da membresia", "Vantagem populacional jovem"], challenges: ["Recursos limitados", "Lacunas de conectividade"] }
    }
  }
} as const;
